import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { useCreateMentorDiscussion } from "@/hooks/useMentorDiscussions";
import { DiscussionImageUpload } from "./DiscussionImageUpload";
import { DiscussionLinkInput, validateLinkUrl } from "./DiscussionLinkInput";

interface CreateMentorDiscussionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateMentorDiscussionDialog({
  open,
  onOpenChange,
}: CreateMentorDiscussionDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const createDiscussion = useCreateMentorDiscussion();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkError, setLinkError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.id) {
      toast({ title: "Error", description: "You must be logged in to create a discussion", variant: "destructive" });
      return;
    }

    if (!title.trim() || !content.trim()) {
      toast({ title: "Error", description: "Please fill in all fields", variant: "destructive" });
      return;
    }

    const urlError = validateLinkUrl(linkUrl);
    if (urlError) { setLinkError(urlError); return; }

    try {
      await createDiscussion.mutateAsync({
        title: title.trim(),
        content: content.trim(),
        userId: user.id,
        image_url: imageUrl || undefined,
        link_url: linkUrl.trim() || undefined,
      });

      toast({ title: "Success", description: "Discussion created successfully" });
      setTitle("");
      setContent("");
      setImageUrl(null);
      setLinkUrl("");
      setLinkError(null);
      onOpenChange(false);
    } catch (error) {
      toast({ title: "Error", description: "Failed to create discussion", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start a Mentor Discussion</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What would you like to discuss?" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">Content</Label>
            <Textarea id="content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share your thoughts with other mentors..." rows={5} />
          </div>

          <DiscussionImageUpload imageUrl={imageUrl} onImageChange={setImageUrl} disabled={createDiscussion.isPending} />

          <DiscussionLinkInput linkUrl={linkUrl} onLinkChange={(v) => { setLinkUrl(v); setLinkError(null); }} error={linkError || undefined} disabled={createDiscussion.isPending} />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={createDiscussion.isPending}>
              {createDiscussion.isPending ? "Creating..." : "Create Discussion"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
